import { useAdminAccess } from "../hooks/useAdminAccess";
import queryString from "query-string";
import { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Spinner } from "src/sudokit/components/Spinner";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { Text } from "src/sudokit/components/Text";
import AddCustomerModal from "./AddCustomerModal";

var dateFormat = require("dateformat");

function Users(props) {
  const { can } = useAdminAccess();
  const [users, setUsers] = useState(null);
  const [searchedUsers, setSearchedUsers] = useState(null);
  const [search, setSearch] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);
  const history = useHistory();
  const location = useLocation();

  // Effect to read search query from URL on component mount and on history.replace
  useEffect(() => {
    const parsed = queryString.parse(location.search);
    const querySearch = parsed.q || "";
    setSearch(querySearch);
    if (querySearch) {
      performSearch(querySearch);
    } else {
      setSearchedUsers(null);
    }
  }, [location]);

  // Separate search logic for reuse
  const performSearch = async (searchTerm) => {
    setIsSearching(true);
    const cleanedSearchTerm = searchTerm.trim();
    if (searchTerm !== cleanedSearchTerm) {
      setSearch(cleanedSearchTerm);
    }
    try {
      const users = await props.userCache.fetchSearched(cleanedSearchTerm);
      if (users) {
        setSearchedUsers(users);
      } else {
        setSearchedUsers(null);
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    history.replace(`/admin/users?q=${encodeURIComponent(search)}`);
  };

  const columns = [
    {
      name: "Photo",
      selector: (row) => {
        return (
          <Link to={"/admin/user/" + row.uid}>
            <img src={row.photoURL || row.providerData?.[0]?.photoURL} />
          </Link>
        );
      },
      sortable: false,
      width: "50px",
      compact: true,
    },
    {
      name: "Email",
      width: "230px",
      selector: (row) => {
        return row.email;
      },
      format: (row) => {
        return <Link to={"/admin/user/" + row.uid}>{row.email}</Link>;
      },
      sortable: true,
    },
    {
      name: "Name",
      selector: (row) => {
        return row.displayName || row.email;
      },
      sortable: true,
    },
    {
      name: "Discord ID",
      selector: (row) => {
        return row.subscription.discordId;
      },
      sortable: true,
    },
    {
      name: "ID",
      selector: (row) => {
        return row.uid;
      },
      sortable: false,
    },
    {
      name: "Sign Up Date",
      width: "100px",
      selector: (row) => {
        if (!row.subscription) {
          return null;
        }

        let date;

        if (row.subscription.data.signedUpAt) {
          date = row.subscription.data.signedUpAt;
        } else {
          date = "None";
        }
        return date;
      },
      format: (row) => {
        if (!row.subscription) {
          return null;
        }

        let date;

        if (row.subscription.data.signedUpAt) {
          date = dateFormat(row.subscription.data.signedUpAt, "mm/dd/yy");
        } else {
          date = "None";
        }
        return date;
      },
      sortable: true,
    },
    {
      name: "Trial Expires",
      selector: (row) => {
        if (!row.subscription) {
          return null;
        }

        return row.subscription.data.trialPeriodEnd;
      },
      format: (row) => {
        if (!row.subscription) {
          return "N/A";
        }

        let date = row.subscription.data.trialPeriodEnd;

        return <span>{dateFormat(date, "mm/dd/yy") + ` (${row.subscription.data.trialDays} day trial)`}</span>;
      },
      sortable: true,
    },
    {
      name: "Pricing Version",
      width: "100px",
      selector: (row) => {
        console.log(row.subscription);
        return row.subscription.data.version;
      },
    },
    {
      name: "Price Offered",
      selector: (row) => {
        if (row.subscription.usesQuota) {
          if (!row.subscription) {
            return 0;
          }

          return parseInt(row.subscription.priceToOffer());
        } else {
          return null;
        }
      },
      format: (row) => {
        if (!row.subscription || row.subscription.usesQuota) {
          return "N/A";
        }

        return `$${row.subscription.priceToOffer()}`;
      },
      sortable: true,
    },
    {
      name: "Cohort",
      selector: (row) => {
        if (!row.subscription) {
          return 0;
        }

        return row.subscription.data.cohort;
      },
      format: (row) => {
        if (!row.subscription) {
          return "";
        }

        return row.subscription.data.cohort;
      },
      sortable: true,
    },
    {
      name: "Membership",
      selector: (row) => {
        if (!row.subscription) {
          return "N/A";
        }

        if (!row.subscription.activeSubscription()) {
          return "No Membership";
        } else {
          return row.subscription.planName();
        }
      },
      minWidth: "200px",

      sortable: true,
    },
    {
      name: "Membership Period End",
      selector: (row) => {
        if (!row.subscription) {
          return null;
        }

        if (!row.subscription.activeSubscription()) {
          return null;
        } else {
          return row.subscription.data.currentPeriodEnd;
        }
      },

      format: (row) => {
        if (!row.subscription) {
          return "N/A";
        }

        if (!row.subscription.activeSubscription()) {
          return "N/A";
        } else {
          let date = row.subscription.data.currentPeriodEnd;
          return dateFormat(date, "mm/dd/yy");
        }
      },
      sortable: true,
    },
    {
      name: "Canceled?",
      selector: (row) => {
        if (!row.subscription) {
          return "N/A";
        }

        if (row.subscription.canceled()) {
          return <span className="red">Yes (canceled on {dateFormat(row.subscription.data.canceledAt, "mm/dd/yy")})</span>;
        } else {
          return "No";
        }
      },
      minWidth: "200px",

      sortable: true,
    },
  ];

  let total, members, trials, canceled;
  if (users) {
    total = users.length;
    members = users.filter((user) => {
      if (!user.subscription) {
        return false;
      }

      return user.subscription.activeSubscription() && !user.subscription.isFreePlan();
    }).length;

    trials = users.filter((user) => {
      if (!user.subscription) {
        return false;
      }

      return !user.subscription.trialExpired() && !user.subscription.activeSubscription();
    }).length;

    canceled = users.filter((user) => {
      if (!user.subscription) {
        return false;
      }

      return user.subscription.canceled();
    }).length;
  }

  return (
    <>
      <h2>Users</h2>

      <div className="users">
        <div className="users-controls">
          <form
            onSubmit={handleSearch}
            className="user-search-form"
          >
            <div className="user-search-group">
              <input
                className="user-search user-search-input"
                type="text"
                placeholder="Search for a user…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <SudoButton
                variant="primary"
                disabled={isSearching}
                className="user-search-button"
              >
                {isSearching ? "Searching..." : "Search"}
              </SudoButton>
            </div>
            <Text
              size="sm"
              color="grey-1"
              className="user-search-hint"
            >
              Search by email, display name, user ID, or Stripe customer ID.
            </Text>
          </form>
          {can("admin.tools") && <SudoButton
            variant="secondary"
            type="button"
            onClick={() => setAddCustomerOpen(true)}
            className='mt-10'
          >
            Add new customer
          </SudoButton>}
        </div>

        {can("admin.tools") && <AddCustomerModal
          open={addCustomerOpen}
          onOpenChange={setAddCustomerOpen}
          onCreated={({ email }) => {
            history.replace(`/admin/users?q=${encodeURIComponent(email)}`);
          }}
        />}

        {isSearching && !searchedUsers && (
          <div className="searching-indicator">
            <Spinner
              size="lg"
              label="Searching users"
            />
            <Text
              size="sm"
              color="grey-1"
              weight="medium"
            >
              Searching for matching users…
            </Text>
          </div>
        )}

        {searchedUsers && (
          <DataTable
            columns={columns}
            responsive={true}
            progressPending={isSearching}
            pagination
            paginationPerPage={30}
            data={searchedUsers}
          />
        )}
      </div>
    </>
  );
}

export default Users;
